// =============================================================================
// MeuDriver — Address autocomplete (Mapbox via lib/geocoding adapter)
// =============================================================================

import { useEffect, useRef, useState, useCallback } from 'react'
import { Loader2, MapPin, X } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { geocode, type GeocodingResult } from '@/lib/geocoding'

export interface AddressAutocompleteProps {
  value: string
  onChange: (value: string, geocoded?: GeocodingResult) => void
  placeholder?: string
}

export function AddressAutocomplete({ value, onChange, placeholder }: AddressAutocompleteProps) {
  const [query, setQuery] = useState(value)
  const [suggestions, setSuggestions] = useState<GeocodingResult[]>([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Sync external value changes (e.g. clearing)
  useEffect(() => { setQuery(value) }, [value])

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const search = useCallback(async (q: string) => {
    if (q.trim().length < 4) { setSuggestions([]); setOpen(false); return }
    setLoading(true)
    try {
      const results = await geocode(q)
      setSuggestions(results)
      setOpen(results.length > 0)
    } finally {
      setLoading(false)
    }
  }, [])

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const val = e.target.value
    setQuery(val)
    onChange(val)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => search(val), 350)
  }

  function handleSelect(r: GeocodingResult) {
    setQuery(r.displayName)
    onChange(r.displayName, r)
    setSuggestions([])
    setOpen(false)
  }

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Input
          placeholder={placeholder}
          value={query}
          onChange={handleInputChange}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          className="h-11 pr-8"
        />
        {loading && (
          <Loader2 className="absolute right-2.5 top-3 h-4 w-4 animate-spin text-muted-foreground" />
        )}
        {!loading && query && (
          <button
            type="button"
            onClick={() => { setQuery(''); onChange(''); setSuggestions([]); setOpen(false) }}
            className="absolute right-2.5 top-3 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {open && suggestions.length > 0 && (
        <ul className="absolute z-50 mt-1 w-full rounded-xl border border-gray-200 bg-white shadow-lg overflow-hidden">
          {suggestions.map((r, idx) => (
            <li key={`${r.lat},${r.lon},${idx}`}>
              <button
                type="button"
                onClick={() => handleSelect(r)}
                className="flex w-full items-start gap-2.5 px-3 py-2.5 text-left hover:bg-brand-50 transition-colors"
              >
                <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0 text-brand-500" />
                <span className="text-sm text-gray-700 leading-snug">
                  {r.displayName}
                </span>
              </button>
            </li>
          ))}
          <li className="border-t border-gray-100 px-3 py-1.5">
            <p className="text-[10px] text-muted-foreground">
              Dados: © Mapbox / OpenStreetMap
            </p>
          </li>
        </ul>
      )}
    </div>
  )
}
